import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../context/AuthContext';
import { OfflineBanner } from '../components/OfflineBanner';
import { getSafePostLoginPath } from '../utils/safePostLoginRedirect';

interface LoginFormData {
  email: string;
  password: string;
}

export function LoginPage() {
  const { login, chooseTenant, clearPendingTenantSelection, pendingTenantSelection } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);
  const [selectingTenant, setSelectingTenant] = useState(false);
  const [tempPasswordExpired, setTempPasswordExpired] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setServerError(null);
    setTempPasswordExpired(false);
    const email = data.email.trim();
    const password = data.password;
    try {
      const result = await login(email, password);
      if (result === 'SUCCESS') {
        navigate(getSafePostLoginPath(searchParams.get('redirect')), { replace: true });
        return;
      }
      setSelectingTenant(true);
      return;
    } catch (productErr: unknown) {
      const code = (productErr as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'TEMP_PASSWORD_EXPIRED') {
        setTempPasswordExpired(true);
        return;
      }
      const ERROR_MESSAGES: Record<string, string> = {
        INVALID_CREDENTIALS: 'Email o password non corretti.',
        ACCOUNT_DISABLED: 'Account disabilitato. Contatta il supporto.',
        RATE_LIMIT_EXCEEDED: 'Troppi tentativi. Riprova tra qualche minuto.',
        TENANT_NOT_FOUND: 'Tenant non trovato.',
      };
      setServerError(code ? (ERROR_MESSAGES[code] ?? 'Si è verificato un errore. Riprova.') : 'Credenziali non valide.');
    }
  };

  const onChooseTenant = async (challengeId: string, tenantId: string) => {
    setServerError(null);
    try {
      await chooseTenant(challengeId, tenantId);
      navigate(getSafePostLoginPath(searchParams.get('redirect')), { replace: true });
    } catch {
      setServerError('Errore nella scelta del tenant. Riprova.');
    }
  };

  const onBackToLogin = () => {
    clearPendingTenantSelection();
    setSelectingTenant(false);
    setServerError(null);
  };

  const showTenantSelection = pendingTenantSelection && selectingTenant;

  return (
    <>
      <OfflineBanner />
      <Container
        maxWidth="xs"
        sx={{
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          py: { xs: 2, sm: 4 },
          gap: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 3 }}>
          {showTenantSelection ? (
            <>
              <Box textAlign="center" mb={2}>
                <Typography variant="h6" color="primary">
                  Scegli tenant
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Account associato a più tenant
                </Typography>
              </Box>
              {serverError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {serverError}
                </Alert>
              )}
              <List disablePadding>
                {pendingTenantSelection.tenants.map((t) => (
                  <ListItemButton
                    key={t.tenantId}
                    onClick={() => onChooseTenant(pendingTenantSelection.challengeId, t.tenantId)}
                  >
                    <ListItemText
                      primary={t.displayName}
                      secondary={`${t.role}`}
                    />
                  </ListItemButton>
                ))}
              </List>
              <Button variant="text" fullWidth onClick={onBackToLogin} sx={{ mt: 2 }}>
                Annulla
              </Button>
            </>
          ) : tempPasswordExpired ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="error" variant="filled" sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Password temporanea scaduta
                </Typography>
                <Typography variant="body2">
                  Contatta il supporto o l&apos;amministratore per ottenere nuove credenziali.
                </Typography>
              </Alert>
              <Button variant="outlined" fullWidth onClick={() => setTempPasswordExpired(false)}>
                Torna al login
              </Button>
            </Box>
          ) : (
            <>
              <Box textAlign="center" mb={3}>
                <Typography variant="h4" fontWeight={800} color="primary">
                  Cantir
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Accedi al tuo account
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Email"
                  type="email"
                  autoComplete="email"
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                  {...register('email', {
                    required: 'Email obbligatoria',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email non valida' },
                  })}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  size="small"
                />

                <TextField
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment> }}
                  {...register('password', { required: 'Password obbligatoria' })}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  size="small"
                />

                {serverError && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
                    {serverError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isSubmitting ? 'Accesso in corso…' : 'Accedi'}
                </Button>
              </Box>

              <Box
                mt={1}
                p={2}
                sx={{
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" mb={1}>
                  Non conosci ancora Cantir?
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  Il gestionale per imprese edili e installatori. Commesse, rapportini, DDT e molto altro.
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  endIcon={<OpenInNewIcon />}
                  href="https://cantir.it"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Scopri Cantir
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </>
  );
}
